import React, { BaseSyntheticEvent, FC, useEffect } from 'react';

import { RuleRead } from '@ariksa/compliance-policies';
import { BlueprintFull } from '@ariksa/compliance-policies/api';
import {
  Box,
  Center,
  Flex,
  HStack,
  ListItem,
  OrderedList,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { each, map, sum } from 'lodash';
import { colorOpacity, customTheme } from 'theme';

import {
  CancelButton,
  PrimaryButton,
  Select,
  SelectOption,
  textAreaFieldStyles,
} from 'components/DataEntry';
import {
  ChatBubbleIcon,
  DetailsIdentifier,
  Drawer,
  IconType,
  IconTypes,
  renderExceptionExpiresOn,
  StackedHeader,
  WithResourceIcon,
} from 'components/index';
import { IDrawerProps } from 'components/Overlay/Drawer/types';
import { useEnvironmentOptions } from 'containers/App/hooks/useEnvironmentOptions';
import { exceptionApplyTimeOptions } from 'containers/Findings/Alerts/Components/utils';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';

export interface ApplyPolicyExceptionProps {
  rule: RuleRead;
  blueprint: BlueprintFull;
  onConfirm(e: BaseSyntheticEvent);
  isLoading?: boolean;
  exceptionDescription: string;
  setExceptionDescription(value: string);
  setExceptionApplyTime(value: SelectOption);
  exceptionApplyTime: SelectOption;
}

interface Props extends ApplyPolicyExceptionProps, IDrawerProps {}

export const ApplyPolicyException: FC<Props> = props => {
  const {
    rule,
    blueprint,
    onConfirm,
    isLoading,
    onClose,
    isOpen,
    setExceptionApplyTime,
    setExceptionDescription,
    exceptionApplyTime,
    exceptionDescription,
    ...rest
  } = props;
  const { environmentMapping } = useEnvironmentOptions();
  const { accountMapping } = useCloudAccountId();
  const [envOptions, setEnvOptions] = React.useState<Record<string, any>[]>([]);
  const [selectedEnvironments, setSelectedEnvironments] = React.useState<
    Record<string, any>[]
  >([]);
  const [accounts, setAccounts] = React.useState(0);

  useEffect(() => {
    let options: Record<string, any>[] = [];
    each(blueprint.environments, o => {
      const env = environmentMapping?.[o];
      if (!!env) options.push({ label: env.name, value: o, data: env });
    });
    setEnvOptions(options);
    setSelectedEnvironments(options);
  }, [environmentMapping, blueprint]);

  useEffect(() => {
    setAccounts(sum(map(selectedEnvironments, o => o.data.account_ids.length)));
  }, [selectedEnvironments]);

  const renderFieldWithIcon = (
    iconType: IconType,
    label: any,
    color: string | undefined,
  ) => {
    return (
      <Box display="inline-block">
        <WithResourceIcon
          resourceType={iconType}
          useCustomColor
          bgColor={colorOpacity(color, 0.2)}
          color={color}
          spacing={2}
        >
          {label}
        </WithResourceIcon>
      </Box>
    );
  };

  return (
    <>
      <Drawer
        onClose={onClose}
        isOpen={isOpen}
        closeButton
        header={
          <StackedHeader
            upper={
              'Apply Exception' +
              (!!rule ? ` for Policy ID: ${rule?.config_id}` : '')
            }
            lower={''}
            type={IconTypes.Exception}
          />
        }
        body={
          <Box w="full" h="full">
            <Stack spacing={5}>
              <DetailsIdentifier
                label="Description"
                value={
                  <Box
                    border="1px solid"
                    borderColor="componentBorder"
                    px={2}
                    py={1}
                    borderRadius={6}
                    h={20}
                    overflowY={'auto'}
                  >
                    {rule.compliance_description}
                  </Box>
                }
                direction="column"
                spacing={1}
              />
              <DetailsIdentifier
                label={renderFieldWithIcon(
                  IconTypes.Policy,
                  'Blueprint That Contains Policy:',
                  customTheme.colors.blue['300'],
                )}
                styles={{
                  value: { pt: 1 },
                }}
                showColon={false}
                value={blueprint.name}
              />
              <DetailsIdentifier
                label={renderFieldWithIcon(
                  IconTypes.Environment,
                  'Environments:',
                  customTheme.colors.primary,
                )}
                showColon={false}
                isInline={false}
                value={
                  <Box w="50%">
                    <Select
                      options={envOptions}
                      value={selectedEnvironments}
                      onChange={setSelectedEnvironments}
                      isMulti
                      menuPortalTarget={document.querySelector('body')}
                      showTotalSelected
                    />
                  </Box>
                }
                flex={1}
              />
              <DetailsIdentifier
                label={renderFieldWithIcon(
                  IconTypes.Account,
                  'Accounts Impacted:',
                  customTheme.colors.gray['300'],
                )}
                styles={{
                  value: { pt: 1 },
                }}
                showColon={false}
                value={accounts}
              />
              <DetailsIdentifier
                label={renderFieldWithIcon(
                  IconTypes.Clock,
                  'Duration For Which Exception Applies:',
                  customTheme.colors.orange['300'],
                )}
                showColon={false}
                value={
                  <Stack>
                    <Box w={'50%'}>
                      <Select
                        value={exceptionApplyTime}
                        options={exceptionApplyTimeOptions}
                        onChange={opt => {
                          setExceptionApplyTime?.(opt);
                        }}
                        menuPortalTarget={document.body}
                      />
                    </Box>
                    {renderExceptionExpiresOn(exceptionApplyTime?.value)}
                  </Stack>
                }
                isInline={false}
              />
              <DetailsIdentifier
                label={
                  <HStack>
                    <Box boxSize={5}>
                      <Center color="primary">
                        <ChatBubbleIcon />
                      </Center>
                    </Box>
                    <Flex fontWeight={600} whiteSpace="nowrap">
                      Reason for exception{' '}
                      <Text color={'red'} pl={1}>
                        *
                      </Text>
                    </Flex>
                  </HStack>
                }
                styles={{ value: { pt: 1 } }}
                showColon={false}
                isInline={false}
                spacing={1}
                value={
                  <Textarea
                    {...textAreaFieldStyles.textArea}
                    //isDisabled={actionType === 'Remove'}
                    placeholder="Provide a reason for the exception"
                    onChange={e => {
                      setExceptionDescription?.(e.target.value.trim());
                    }}
                  />
                }
              />
              <Stack>
                <Text color={'primary'}>NOTE:</Text>
                <OrderedList spacing={2} fontSize="sm">
                  <ListItem pl={3}>
                    Applying this exception will automatically resolve all
                    current violations with “Exception” as the reason
                  </ListItem>
                  <ListItem pl={3}>
                    When the exception expires, this environment will be
                    automatically assessed for violations associated with this
                    policy
                  </ListItem>
                </OrderedList>
              </Stack>
            </Stack>
          </Box>
        }
        footer={
          <Flex justifyContent={'space-between'} w="full">
            <CancelButton onClick={onClose}>CANCEL</CancelButton>
            <PrimaryButton
              onClick={e => onConfirm(e)}
              isLoading={isLoading}
              isDisabled={!exceptionDescription?.trim()}
            >
              OK
            </PrimaryButton>
          </Flex>
        }
        styles={{ drawer: { size: 'xl' } }}
        {...rest}
      />
    </>
  );
};
